import { useState } from "react";
import { Alert, Box, Button, Checkbox, FormControl, Grid, InputLabel, Link, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { getItems } from "../../../api/items";
import { useUser } from "../../../api/useAuth";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardDateFilter } from "../../../dashboard-framework/DashboardDateFilter";
import { useDrillDownDialog } from "../../../dashboard-framework/DrillDownProvider";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { formatQuantity } from "../../../dashboard-framework/format";
import { DataTableCard } from "../../../dashboard-framework/widgets/DataTableCard";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { DrillDownConfig, GlobalFilterValues } from "../../../dashboard-framework/types";
import { fetchAllItemPages } from "../shared/fetchAllItemPages";
import {
    ALL_REPORT_SERVICE_IDS,
    DAYS_BADGE_GREEN,
    DAYS_BADGE_GREEN_MAX,
    DAYS_BADGE_RED,
    DAYS_BADGE_YELLOW,
    DAYS_BADGE_YELLOW_MAX,
    DEFAULT_SERVICE_TYPE_KEYS,
    describePendingListPt,
    describeStaleConsultationsPt,
    describeSummaryPt,
    describeTechnicianTablePt,
    DRILL_FIELDS,
    drillTreatmentRoute,
    EVALUATION_FIELDS,
    formatAverageDays,
    overduePendingFilter,
    PEOPLE_FIELDS,
    PEOPLE_LOOKUP_CHUNK_SIZE,
    pendingDrillColumns,
    pendingEvaluationsFilter,
    QEEG_PERIOD_PRESETS,
    reportServiceTypeKey,
    sentDrillColumns,
    sentEvaluationsFilter,
    SERVICE_TYPE_OPTIONS,
    ServiceTypeKey,
    serviceTypesLabel,
    serviceTypeShortLabel,
    STALE_DRILL_FIELDS,
    staleConsultationsFilter,
    staleDrillColumns,
} from "./qeegPendingReportsConfig";
import {
    derivePendingCases,
    deriveSentCases,
    lisbonDayKey,
    OVERDUE_DAYS,
    PendingCaseRow,
    PersonInfo,
    SummaryTotals,
    summaryTotals,
    TechnicianStatsRow,
    technicianStatsRows,
} from "./qeegPendingReportsRows";

const FIVE_MINUTES = 5 * 60 * 1000;

// Universos acima disto (15 páginas × 1000) disparam o aviso de demasiados registos.
const QEEG_MAX_PAGES = 15;

const formatDayPt = (dayKey: string) => {
    const formatted = format(parseISO(dayKey), "dd/MM/yyyy");
    return formatted;
};

// A rota /users ignora limit/page (createRoute) e responde com ≤100 — lookup em blocos de ids.
const fetchPeopleByIds = async (personIds: Array<string>) => {
    const idChunks: Array<Array<string>> = [];
    for (let chunkStart = 0; chunkStart < personIds.length; chunkStart += PEOPLE_LOOKUP_CHUNK_SIZE) {
        idChunks.push(personIds.slice(chunkStart, chunkStart + PEOPLE_LOOKUP_CHUNK_SIZE));
    }
    const chunkResults = await Promise.all(
        idChunks.map((idChunk) => getItems("directus_users", PEOPLE_FIELDS, { filter: { id: { _in: idChunk } }, sort: "first_name" }))
    );
    const people = chunkResults.flatMap((chunkResult) => chunkResult.items);
    return people;
};

type DaysWaitingBadgeProps = {
    days: number;
};

const DaysWaitingBadge = ({ days }: DaysWaitingBadgeProps) => {
    const badgeColor = days <= DAYS_BADGE_GREEN_MAX ? DAYS_BADGE_GREEN : days <= DAYS_BADGE_YELLOW_MAX ? DAYS_BADGE_YELLOW : DAYS_BADGE_RED;
    return (
        <Box
            component="span"
            sx={{
                bgcolor: badgeColor,
                color: "#fff",
                px: 1.25,
                py: 0.25,
                borderRadius: "14px",
                fontWeight: 600,
                fontSize: 13,
                display: "inline-block",
                minWidth: 30,
                textAlign: "center",
            }}
        >
            {days}
        </Box>
    );
};

const treatmentCell = (treatmentId: number | null) => {
    if (!treatmentId) {
        return <Typography variant="body2" component="span" color="text.secondary">—</Typography>;
    }
    return (
        <Link href={`/treatments/${treatmentId}/detail`} target="_blank" rel="noopener" sx={{ color: "#185FA5", fontWeight: 600 }}>
            {treatmentId}
        </Link>
    );
};

// Flag de dados — componente próprio para o hook do drill resolver DENTRO do DrillDownProvider
// (que vive no DashboardPage), e não no topo do dashboard onde o contexto ainda é null.
type StaleConsultationsAlertProps = {
    count: number;
    drill: DrillDownConfig;
};

const StaleConsultationsAlert = ({ count, drill }: StaleConsultationsAlertProps) => {
    const drillDownDialog = useDrillDownDialog();
    const label =
        count === 1
            ? "consulta de avaliação agendada com data já passada e por fechar"
            : "consultas de avaliação agendadas com data já passada e por fechar";
    return (
        <Alert
            severity="warning"
            action={
                drillDownDialog ? (
                    <Button color="inherit" size="small" onClick={() => drillDownDialog.openDrillDown(drill)}>
                        Ver
                    </Button>
                ) : undefined
            }
        >
            {count} {label} — atualiza o estado da sessão (concluída / remarcada / cancelada) para regularizar os dados.
        </Alert>
    );
};

const technicianTableColumns = [
    { key: "technician", label: "Técnico" },
    { key: "overdue", label: "Em atraso", isNumeric: true },
    { key: "pending", label: "Pendentes", isNumeric: true },
    { key: "done", label: "Feitos", isNumeric: true },
    { key: "averageTime", label: "Tempo médio", isNumeric: true },
];

const buildTechnicianTableRows = (statsRows: Array<TechnicianStatsRow>) => {
    const rows = statsRows.map((statsRow) => {
        const displayRow: Record<string, React.ReactNode> = {
            technician: statsRow.displayName,
            overdue: (
                <Typography variant="body2" component="span" fontWeight={600} color={statsRow.emAtraso > 0 ? DAYS_BADGE_RED : undefined}>
                    {statsRow.emAtraso}
                </Typography>
            ),
            pending: statsRow.pendentes,
            done: statsRow.feitos,
            averageTime: formatAverageDays(statsRow.tempoMedioDays),
        };
        return displayRow;
    });
    return rows;
};

const technicianTotalsRow = (totals: SummaryTotals) => {
    const row = {
        technician: "Total",
        overdue: formatQuantity(totals.emAtraso),
        pending: formatQuantity(totals.pendentes),
        done: formatQuantity(totals.feitos),
        averageTime: formatAverageDays(totals.tempoMedioDays),
    };
    return row;
};

const casesOfType = <CaseRow extends { reportServiceId: number | null }>(cases: Array<CaseRow>, serviceTypeKey: ServiceTypeKey) => {
    const filtered = cases.filter((caseRow) => reportServiceTypeKey(caseRow.reportServiceId) === serviceTypeKey);
    return filtered;
};

export const QeegPendingReportsDashboard = () => {
    const access = useDashboardAccess();
    const userResult = useUser();
    const [periodValues, setPeriodValues] = useState<GlobalFilterValues>({ period: "ultimos_30_dias" });
    const [serviceTypeKeys, setServiceTypeKeys] = useState<Array<ServiceTypeKey>>(DEFAULT_SERVICE_TYPE_KEYS);
    const setPeriodValue = (filterKey: string, value: string | number | null) => {
        setPeriodValues((currentValues) => ({ ...currentValues, [filterKey]: value }));
    };

    // Vista pessoal (viewer_rules → params.technicianUserId): tudo filtrado ao próprio psicólogo.
    // Enquanto o $current_user não resolve, as queries ficam parqueadas (nunca mostrar tudo).
    const currentUserId: string | null = userResult.data?.id || null;
    const viewerParamValue = access.viewerParams?.technicianUserId;
    const ownTechnicianUserId =
        viewerParamValue === undefined ? null : viewerParamValue === "$current_user" ? currentUserId : String(viewerParamValue);
    const isOwnView = viewerParamValue !== undefined;
    const viewerPending = isOwnView && ownTechnicianUserId === null;

    const now = new Date();
    const periodRange = resolveRequiredDateFilterRange(periodValues, "period", "ultimos_30_dias", now);
    // "Hoje" nunca deriva do período: com presets de período completo anterior, o lt pode estar no passado.
    const todayDayKey = lisbonDayKey(now.toISOString()) || format(now, "yyyy-MM-dd");

    // O fetch traz SEMPRE ambos os tipos (cartões + tabelas por técnico mostram o quadro completo);
    // o multiselect só recorta a lista de detalhe, client-side.
    const bothTypesLabel = serviceTypesLabel(["first", "followup"]);
    const detailTypesLabel = serviceTypesLabel(serviceTypeKeys);
    const pendingFilter = pendingEvaluationsFilter(ownTechnicianUserId, ALL_REPORT_SERVICE_IDS);
    const sentFilter = sentEvaluationsFilter(periodRange, ownTechnicianUserId, ALL_REPORT_SERVICE_IDS);
    const staleFilter = staleConsultationsFilter(ownTechnicianUserId, ALL_REPORT_SERVICE_IDS, now);

    const pendingResult = useQuery({
        queryKey: ["qeeg-pending-reports-pending", JSON.stringify(pendingFilter)],
        queryFn: () => fetchAllItemPages("evaluations", EVALUATION_FIELDS, pendingFilter, { maxPages: QEEG_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled: !viewerPending,
    });
    const sentResult = useQuery({
        queryKey: ["qeeg-pending-reports-sent", JSON.stringify(sentFilter)],
        queryFn: () => fetchAllItemPages("evaluations", EVALUATION_FIELDS, sentFilter, { maxPages: QEEG_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled: !viewerPending,
    });
    const staleResult = useQuery({
        queryKey: ["qeeg-pending-reports-stale", JSON.stringify(staleFilter)],
        queryFn: () => fetchAllItemPages("evaluations", STALE_DRILL_FIELDS, staleFilter, { maxPages: QEEG_MAX_PAGES }),
        staleTime: FIVE_MINUTES,
        keepPreviousData: true,
        enabled: !viewerPending,
    });

    const pendingEvaluations = pendingResult.data?.items || [];
    const sentEvaluations = sentResult.data?.items || [];
    const reachedPageLimit = Boolean(pendingResult.data?.reachedPageLimit || sentResult.data?.reachedPageLimit);
    const staleCount = staleResult.data?.items?.length || 0;

    const technicianUserIds = Array.from(
        new Set(
            [...pendingEvaluations, ...sentEvaluations]
                .map((evaluation: any) => evaluation.report_session?.technician?.user_id)
                .filter((userId: any) => Boolean(userId))
        )
    ).sort();

    const peopleResult = useQuery({
        queryKey: ["qeeg-pending-reports-people", technicianUserIds.join(",")],
        queryFn: () => fetchPeopleByIds(technicianUserIds as Array<string>),
        enabled: technicianUserIds.length > 0,
        staleTime: FIVE_MINUTES,
    });
    const peopleById: Record<string, PersonInfo> = {};
    const people: Array<PersonInfo> = Array.isArray(peopleResult.data) ? peopleResult.data : [];
    people.forEach((person) => {
        peopleById[person.id] = person;
    });

    const pendingCases = derivePendingCases(pendingEvaluations, peopleById, todayDayKey);
    const sentCases = deriveSentCases(sentEvaluations, peopleById);

    // Cartões: quadro completo (ambos os tipos). Período aplica-se a Feitos/Tempo médio.
    const totals = summaryTotals(pendingCases, sentCases);

    // Tabelas por técnico: uma por tipo (sempre ambas na vista de gestão).
    const firstPendingCases = casesOfType(pendingCases, "first");
    const followupPendingCases = casesOfType(pendingCases, "followup");
    const firstSentCases = casesOfType(sentCases, "first");
    const followupSentCases = casesOfType(sentCases, "followup");
    const firstStats = technicianStatsRows(firstPendingCases, firstSentCases);
    const followupStats = technicianStatsRows(followupPendingCases, followupSentCases);
    const firstTotals = summaryTotals(firstPendingCases, firstSentCases);
    const followupTotals = summaryTotals(followupPendingCases, followupSentCases);

    // Lista de detalhe: filtrada pelo multiselect (client-side).
    const detailPendingCases: Array<PendingCaseRow> = pendingCases.filter((pendingCase) => {
        const typeKey = reportServiceTypeKey(pendingCase.reportServiceId);
        const included = typeKey !== null && serviceTypeKeys.includes(typeKey);
        return included;
    });

    const isLoading =
        viewerPending ||
        pendingResult.isLoading ||
        sentResult.isLoading ||
        (technicianUserIds.length > 0 && peopleResult.isLoading);
    const isError = pendingResult.isError || sentResult.isError || peopleResult.isError;

    // Drill dos cartões (modal "ver dados subjacentes") — quadro completo, gated por drill_roles;
    // as linhas abrem o tratamento em novo separador (window.open no diálogo).
    const overdueDrill: DrillDownConfig = {
        kind: "query",
        title: `Em atraso (+${OVERDUE_DAYS} dias)`,
        entity: "evaluations",
        fieldsQuery: DRILL_FIELDS,
        filter: overduePendingFilter(ownTechnicianUserId, ALL_REPORT_SERVICE_IDS, now),
        defaultSort: "report_session.start_date",
        columns: pendingDrillColumns,
        rowRoute: drillTreatmentRoute,
        queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel),
    };
    const pendingDrill: DrillDownConfig = {
        kind: "query",
        title: "Pendentes (por enviar)",
        entity: "evaluations",
        fieldsQuery: DRILL_FIELDS,
        filter: pendingFilter,
        defaultSort: "report_session.start_date",
        columns: pendingDrillColumns,
        rowRoute: drillTreatmentRoute,
        queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel),
    };
    const sentDrill: DrillDownConfig = {
        kind: "query",
        title: "Relatórios feitos no período",
        entity: "evaluations",
        fieldsQuery: DRILL_FIELDS,
        filter: sentFilter,
        defaultSort: "-evaluation_date_sent",
        columns: sentDrillColumns,
        rowRoute: drillTreatmentRoute,
        queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel),
    };
    const tempoMedioDrill: DrillDownConfig = { ...sentDrill, title: "Tempo médio — relatórios enviados no período" };
    const staleDrill: DrillDownConfig = {
        kind: "query",
        title: "Consultas agendadas por regularizar",
        entity: "evaluations",
        fieldsQuery: STALE_DRILL_FIELDS,
        filter: staleFilter,
        defaultSort: "report_session.start_date",
        columns: staleDrillColumns,
        rowRoute: drillTreatmentRoute,
        queryDescription: describeStaleConsultationsPt(),
    };

    const pendingListColumns = [
        ...(isOwnView ? [] : [{ key: "technician", label: "Técnico" }]),
        { key: "treatment", label: "Tratamento" },
        { key: "daysWaiting", label: "Dias à espera", isNumeric: true },
        { key: "consultationDate", label: "Data da consulta" },
        { key: "service", label: "Serviço" },
    ];

    const pendingListRows = detailPendingCases.map((pendingCase) => {
        const displayRow: Record<string, React.ReactNode> = {
            technician: pendingCase.technicianName,
            treatment: treatmentCell(pendingCase.treatmentId),
            daysWaiting: <DaysWaitingBadge days={pendingCase.daysWaiting} />,
            consultationDate: formatDayPt(pendingCase.consultationDayKey),
            service: serviceTypeShortLabel(reportServiceTypeKey(pendingCase.reportServiceId)),
        };
        return displayRow;
    });

    return (
        <DashboardPage globalFilters={[]} values={{}} onChange={() => undefined}>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
                <DashboardDateFilter label="Período" filterKey="period" values={periodValues} onChange={setPeriodValue} presets={QEEG_PERIOD_PRESETS} />
                <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="qeeg-type-label">Tipo de avaliação</InputLabel>
                    <Select
                        labelId="qeeg-type-label"
                        label="Tipo de avaliação"
                        multiple
                        value={serviceTypeKeys}
                        onChange={(event) => {
                            const value = event.target.value;
                            const nextKeys = (typeof value === "string" ? value.split(",") : value) as Array<ServiceTypeKey>;
                            if (nextKeys.length > 0) setServiceTypeKeys(nextKeys);
                        }}
                        renderValue={(selected) => serviceTypesLabel(selected as Array<ServiceTypeKey>)}
                    >
                        {SERVICE_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                                <Checkbox checked={serviceTypeKeys.includes(option.key)} size="small" />
                                <ListItemText primary={option.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                    O período aplica-se a Feitos e Tempo médio. O tipo de avaliação filtra apenas a lista de detalhe em baixo. Em atraso e Pendentes mostram sempre todos os casos (consulta desde 01/01/2026).
                </Typography>
            </Box>

            {reachedPageLimit && (
                <Alert severity="warning">
                    Há demasiadas avaliações para o período escolhido e os números podem estar incompletos — encurta o período.
                </Alert>
            )}

            {staleCount > 0 && <StaleConsultationsAlert count={staleCount} drill={staleDrill} />}

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        label={`Em atraso (+${OVERDUE_DAYS} dias)`}
                        value={isLoading ? undefined : formatQuantity(totals.emAtraso)}
                        color={DASHBOARD_COLORS.cancelled}
                        explanation={{
                            queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel),
                            businessNote: "Casos por enviar há mais de 10 dias — os que mais urge resolver.",
                        }}
                        isLoading={isLoading}
                        drillDown={overdueDrill}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        label="Pendentes (por enviar)"
                        value={isLoading ? undefined : formatQuantity(totals.pendentes)}
                        color={DASHBOARD_COLORS.orange}
                        explanation={{ queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel) }}
                        isLoading={isLoading}
                        drillDown={pendingDrill}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        label="Relatórios feitos"
                        value={isLoading ? undefined : formatQuantity(totals.feitos)}
                        color={DASHBOARD_COLORS.done}
                        explanation={{ queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel) }}
                        isLoading={isLoading}
                        drillDown={sentDrill}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        label="Tempo médio de envio"
                        value={isLoading ? undefined : formatAverageDays(totals.tempoMedioDays)}
                        color={DASHBOARD_COLORS.highlight}
                        explanation={{ queryDescription: describeSummaryPt(periodRange, isOwnView, bothTypesLabel) }}
                        isLoading={isLoading}
                        drillDown={tempoMedioDrill}
                    />
                </Grid>
            </Grid>

            {!isOwnView && (
                <DataTableCard
                    title={`${serviceTypeShortLabel("first")} — por técnico`}
                    explanation={{ queryDescription: describeTechnicianTablePt(periodRange, serviceTypeShortLabel("first")) }}
                    columns={technicianTableColumns}
                    rows={buildTechnicianTableRows(firstStats)}
                    totalsRow={technicianTotalsRow(firstTotals)}
                    maxBodyHeight={360}
                    isLoading={isLoading}
                    isError={isError}
                />
            )}

            {!isOwnView && (
                <DataTableCard
                    title={`${serviceTypeShortLabel("followup")} — por técnico`}
                    explanation={{ queryDescription: describeTechnicianTablePt(periodRange, serviceTypeShortLabel("followup")) }}
                    columns={technicianTableColumns}
                    rows={buildTechnicianTableRows(followupStats)}
                    totalsRow={technicianTotalsRow(followupTotals)}
                    maxBodyHeight={360}
                    isLoading={isLoading}
                    isError={isError}
                />
            )}

            <DataTableCard
                title={isOwnView ? "Os meus por enviar — do mais antigo ao mais recente" : "Por enviar — do mais antigo ao mais recente"}
                explanation={{ queryDescription: describePendingListPt(isOwnView, detailTypesLabel) }}
                columns={pendingListColumns}
                rows={pendingListRows}
                maxBodyHeight={560}
                isLoading={isLoading}
                isError={isError}
            />
        </DashboardPage>
    );
};
